import type { DeskType } from '@cocoonspace/sdk-js/types/deskType'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { EventOverstay } from '@cocoonspace/sdk-js/types/eventOverstay'
import type { EventStatus } from '@cocoonspace/sdk-js/types/eventStatus'
import type { EventType } from '@cocoonspace/sdk-js/types/eventType'
import type { Image } from '@cocoonspace/sdk-js/types/image'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { BookingRequest } from '@cocoonspace/sdk-js/v2/types/bookingRequest'
import { useMemo } from 'react'
import { useListEvents } from '@cocoonspace/sdk-js/domains/events/events'
import { useListBookingRequests } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { keepPreviousData } from '@tanstack/react-query'

export interface EventItem {
	originalEvent?: Event
	id: string
	eventType: 'cocoon' | 'bookingRequest'
	type?: EventType
	status?: EventStatus
	title?: string
	startLocal?: string
	endLocal?: string
	timezone: string
	totalPrice?: number
	booking_ref?: string
	location: {
		type: 'cocoon' | 'remote'
		name?: string
		address?: {
			address1: string
			address2?: string
			postal_code: string
			city: string
		}
		images?: Image[]
	}
	space?: Space
	isScheduler?: boolean
	isOrganizer?: boolean
	isGuest?: boolean
	guests: string[]

	overstay?: EventOverstay

	googleCalendar: {
		editLink?: string
		htmlLink?: string
		meetLink?: string
	}

	// @deprecated
	deskQty?: number
	deskType?: DeskType
}

export const fromCocoonEventToEventItem = (
	event: Event,
	bookingRequest?: BookingRequest,
): EventItem => {
	const googleCalendar = {
		editLink: bookingRequest?.googleCalendarEditLink as string | undefined,
		htmlLink: bookingRequest?.googleCalendarHtmlLink as string | undefined,
		meetLink: bookingRequest?.googleMeetLink as string | undefined,
	}

	return {
		originalEvent: event,
		id: event.id,
		eventType: 'cocoon',
		type: event.type,
		status: event.status,
		title: event.title,
		startLocal: event.start_local,
		endLocal: event.end_local,
		totalPrice: event.total_price,
		timezone: event.space?.timezone ?? 'Europe/Paris',
		location: {
			type: 'cocoon',
			name: event.space?.title ?? '',
			address: event.space?.address,
			images: event.space?.images,
		},
		space: event.space,
		isScheduler: !!bookingRequest,
		isOrganizer: event.is_organizer,
		isGuest: event.is_guest,

		googleCalendar,

		// Overstay
		overstay: event.overstay,

		booking_ref: event?.booking_ref,
		guests: event.guests?.map((guest) => guest.email) ?? [],

		// @deprecated
		deskQty: event.qty,
		deskType: event.space?.desk_type,
	} satisfies EventItem
}

const fromBookingRequestToEventItem = (
	bookingRequest: BookingRequest,
): EventItem => {
	const [startHour, startMinute] = bookingRequest.start.split(':')
	const [endHour, endMinute] = bookingRequest.end.split(':')

	const googleCalendar = {
		editLink: bookingRequest?.googleCalendarEditLink as string | undefined,
		htmlLink: bookingRequest?.googleCalendarHtmlLink as string | undefined,
		meetLink: bookingRequest?.googleMeetLink as string | undefined,
	}

	return {
		id: bookingRequest.id,
		title: bookingRequest.title,
		// status: bookingRequest.status,
		timezone: bookingRequest.timezone || 'Europe/Paris',
		location: {
			type: bookingRequest.location?.type as 'cocoon' | 'remote',
		},
		guests: [bookingRequest.email],
		isScheduler: true,
		eventType: 'bookingRequest',
		startLocal: dateTimeUtils(bookingRequest.day)
			.hour(Number(startHour))
			.minute(Number(startMinute))
			.tz(bookingRequest?.timezone || 'Europe/Paris')
			.format('YYYY-MM-DD HH:mm'),
		endLocal: dateTimeUtils(bookingRequest.day)
			.hour(Number(endHour))
			.minute(Number(endMinute))
			.tz(bookingRequest?.timezone || 'Europe/Paris')
			.format('YYYY-MM-DD HH:mm'),

		googleCalendar,
	} satisfies EventItem
}

export const useAllEvents = (filters: {
	include?: 'current' | 'past'
	year?: number
	from?: string
	to?: string
}) => {
	const {
		data: events,
		isLoading: isLoadingEvents,
		isError: isErrorEvents,
	} = useListEvents(
		{
			include: filters.include,
			year: filters.year || undefined,
			from: filters.from || undefined,
			to: filters.to || undefined,
		},
		{ query: { placeholderData: keepPreviousData } },
	)

	const {
		data: bookingRequests,
		isLoading: isLoadingBookingRequests,
		isError: isErrorBookingRequests,
	} = useListBookingRequests(
		{ status: 'validated' },
		{ query: { placeholderData: keepPreviousData } },
	)

	const allEvents = useMemo<EventItem[]>(() => {
		const cocoonEvents =
			events?.data?.map((event) => {
				const bookingRequest = bookingRequests?.data.find(
					({ bookingId }) => bookingId === event.booking_id,
				)

				return fromCocoonEventToEventItem(event, bookingRequest)
			}) || []

		const bookingRequestsEvents =
			bookingRequests?.data
				?.filter(({ location }) => !location?.space_ids)
				.map((bookingRequest) => fromBookingRequestToEventItem(bookingRequest))
				.filter(({ startLocal }) => {
					const start = dateTimeUtils(startLocal)

					if (filters.year && start.year() !== filters.year) {
						return false
					}

					if (filters.include) {
						const now = dateTimeUtils()

						if (filters.include === 'past') return start.isBefore(now)
						if (filters.include === 'current') return start.isAfter(now)
					}

					if (filters.from && filters.to) {
						const from = dateTimeUtils(filters.from)
						const to = dateTimeUtils(filters.to)

						return start.isBetween(from, to, 'day', '[]')
					}

					return true
				}) || []

		return [...cocoonEvents, ...bookingRequestsEvents].sort((a, b) => {
			const aDate = dateTimeUtils(a.startLocal)
			const bDate = dateTimeUtils(b.startLocal)

			return aDate.isBefore(bDate) ? -1 : 1
		})
	}, [
		events?.data,
		bookingRequests,
		filters.include,
		filters.year,
		filters.from,
		filters.to,
	])

	return {
		isLoading: isLoadingEvents || isLoadingBookingRequests,
		isError: isErrorEvents || isErrorBookingRequests,
		events: allEvents,
	}
}
